import type { ApiClient, OrderRecord, components } from '@adiox/api';
import { describeRejection, isRetryableStatus, newIdempotencyKey, type Rejection } from './tradeRules';

export type TradeResponse = components['schemas']['TradeResponse'];
export type OrderRequest = components['schemas']['OrderRequest'];
export type ModifyOrderRequest = components['schemas']['ModifyOrderRequest'];
export type CloseAllResponse = components['schemas']['CloseAllResponse'];
export type CloseAllRequest = components['schemas']['CloseAllRequest'];
export type CloseByAllResponse = components['schemas']['CloseByAllResponse'];
export type CancelAllResponse = components['schemas']['CancelAllResponse'];

export type Outcome<T> = ({ ok: true } & { value: T }) | ({ ok: false } & Rejection);

export interface TradeClientOptions {
  /** How many times a keyed request is repeated after a transport failure or 429/503. */
  retries?: number;
  delayMs?: number;
  sleep?: (ms: number) => Promise<void>;
}

interface Result {
  data?: unknown;
  error?: unknown;
  response?: { status: number };
}

/** The complete modify request for an order: everything as it is now, with the changes on top. */
export function fullOrderBody(
  order: OrderRecord,
  patch: Partial<Pick<ModifyOrderRequest, 'price' | 'stopLoss' | 'takeProfit'>>,
): ModifyOrderRequest {
  return {
    price: order.priceOrder,
    stopLimitPrice: order.priceStopLimit,
    stopLoss: order.stopLoss,
    takeProfit: order.takeProfit,
    expiration: order.expiration,
    expirationTime: order.expirationTime ?? null,
    ...patch,
  };
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Sends trading requests for one account. Requests that open or close positions carry an Idempotency-Key, and a repeat
 * after a lost response reuses it, so a retry after a timeout can never open two positions: the server returns the first
 * result. Requests without a key (modify, cancel) are never repeated automatically.
 */
export function createTradeClient(api: ApiClient, login: number, options: TradeClientOptions = {}) {
  const retries = options.retries ?? 2;
  const delay = options.delayMs ?? 400;
  const sleep = options.sleep ?? wait;
  const path = { login };
  // The generated client types do not list the Idempotency-Key header, so requests go through one loosely typed door.
  const call = api as unknown as Record<
    'POST' | 'PUT' | 'DELETE',
    (url: string, init: Record<string, unknown>) => Promise<Result>
  >;

  async function send<T>(
    method: 'POST' | 'PUT' | 'DELETE',
    url: string,
    init: Record<string, unknown>,
    keyed: boolean,
  ): Promise<Outcome<T>> {
    let attempt = 0;
    for (;;) {
      // A transport failure gives null: the server may or may not have acted.
      const result: Result | null = await call[method](url, init).catch(() => null);
      const status = result?.response?.status;
      if (result && result.error === undefined && result.data !== undefined) {
        return { ok: true, value: result.data as T };
      }
      if (keyed && attempt < retries && isRetryableStatus(status)) {
        attempt += 1;
        await sleep(delay * attempt);
        continue;
      }
      if (!result) {
        return {
          ok: false,
          message: keyed
            ? 'The connection failed and the result is unknown. Check the trade list before trying again.'
            : 'The connection failed. Nothing was changed as far as we know; check the trade list.',
        };
      }
      return { ok: false, ...describeRejection(status, result.error) };
    }
  }

  const headers = (key: string) => ({ 'Idempotency-Key': key });

  return {
    /** Places a market or pending order. Pass the same `key` to repeat an attempt the user believes did not go through. */
    placeOrder: (body: OrderRequest, key = newIdempotencyKey()) =>
      send<TradeResponse>(
        'POST',
        '/api/v1/accounts/{login}/orders',
        { params: { path }, body, headers: headers(key) },
        true,
      ),
    /**
     * Changes a pending order. The server treats every level left out as "remove it", so the request always carries the
     * order's current stop-limit price, levels and expiration, with only `patch` changed.
     */
    modifyOrderFrom: (
      order: OrderRecord,
      patch: Partial<Pick<ModifyOrderRequest, 'price' | 'stopLoss' | 'takeProfit'>>,
    ) =>
      send<TradeResponse>(
        'PUT',
        '/api/v1/accounts/{login}/orders/{ticket}',
        {
          params: { path: { ...path, ticket: order.ticket } },
          body: fullOrderBody(order, patch),
        },
        false,
      ),
    cancelOrder: (ticket: number) =>
      send<TradeResponse>(
        'DELETE',
        '/api/v1/accounts/{login}/orders/{ticket}',
        { params: { path: { ...path, ticket } } },
        false,
      ),
    /** 0 removes a level. `trailingPoints` (0: off) is left as it is when not given. */
    modifyPosition: (ticket: number, stopLoss: number, takeProfit: number, trailingPoints?: number) =>
      send<TradeResponse>(
        'PUT',
        '/api/v1/accounts/{login}/positions/{ticket}',
        {
          params: { path: { ...path, ticket } },
          body: { stopLoss, takeProfit, ...(trailingPoints === undefined ? {} : { trailingPoints }) },
        },
        false,
      ),
    /** Closes a position against an opposite one of the same symbol, for the volume they share. */
    closeBy: (ticket: number, opposite: number, key = newIdempotencyKey()) =>
      send<TradeResponse>(
        'POST',
        '/api/v1/accounts/{login}/positions/{ticket}/close-by/{opposite}',
        { params: { path: { ...path, ticket, opposite } }, headers: headers(key) },
        true,
      ),
    /** Multiple Close By: every buy against sells of the symbol (or of all symbols), oldest first. */
    closeByAll: (symbol?: string) =>
      send<CloseByAllResponse>(
        'POST',
        '/api/v1/accounts/{login}/positions/close-by-all',
        { params: { path }, body: symbol ? { symbol } : {} },
        false,
      ),
    /** Deletes all pending orders (of one symbol when given). */
    cancelAll: (symbol?: string) =>
      send<CancelAllResponse>(
        'POST',
        '/api/v1/accounts/{login}/orders/cancel-all',
        { params: { path }, body: symbol ? { symbol } : {} },
        false,
      ),
    /** Omit `volume` for a full close. */
    closePosition: (ticket: number, volume?: number, key = newIdempotencyKey()) =>
      send<TradeResponse>(
        'POST',
        '/api/v1/accounts/{login}/positions/{ticket}/close',
        { params: { path: { ...path, ticket } }, body: volume ? { volume } : {}, headers: headers(key) },
        true,
      ),
    /** Every open position, or only the profitable or losing ones, optionally of one symbol (the server picks them). */
    closeAll: (which: CloseAllRequest = {}) =>
      send<CloseAllResponse>(
        'POST',
        '/api/v1/accounts/{login}/positions/close-all',
        { params: { path }, body: which },
        false,
      ),
  };
}

export type TradeClient = ReturnType<typeof createTradeClient>;
